import React, { useCallback, useState } from "react";
import { Coins, LoaderCircle, ReceiptText } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { getPublicVideoLabel } from "../../utils/projectRuntime";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

const SOURCE_LABELS = {
  ai_chat: "文字助手",
  agent_chat: "文字助手",
  adaptation_agent_stage: "智能改编",
  character_workshop: "角色工坊",
  character_generation: "角色生成",
  scene_workshop: "场景工坊",
  scene_generation: "场景生成",
  prop_generation: "道具生成",
  episode_mini_storyboard: "小故事板",
  episode_mini_storyboard_video: "小故事板视频",
  episode_storyboard_board: "故事板导演台",
  project_outline: "剧情大纲",
  script_analysis_characters: "角色拆分",
  script_analysis_scenes: "场景拆分",
  script_analysis_props: "道具拆分",
  storyboard_batch_expand: "故事板批量生成",
  storyboard_sequential_images: "连续故事板",
  super_resolution_sync_legacy: "视频超分",
  image_generation: "图片生成",
  simple_image: "图片生成",
  video_generation: "视频生成",
  music_generation: "音乐生成",
  tts_generate: "语音生成",
};

const formatPoints = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? Math.round(number).toLocaleString("zh-CN", { maximumFractionDigits: 0 })
    : "--";
};

const formatTime = (value) => {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(timestamp));
};

const formatQuotaDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  return `${Number(match[2])}月${Number(match[3])}日`;
};

const resolveSourceLabel = (item) => {
  const source = String(item?.source || "").trim();
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  if (!source) return "AI 生成";
  return source
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const UserPointBalance = ({
  summary,
  balance,
  accentColor = "var(--color-accent-primary)",
  className = "",
  showLabel = true,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [instantPopover, setInstantPopover] = useState(false);
  const popoverVisibility = useDelayedUnmount(open, 110, 100);
  const totalBalance = summary?.balance ?? balance;
  const dailyRemaining = summary?.dailyRemaining;
  const dailyQuota = summary?.dailyQuota;
  const permanentBalance = summary?.permanentBalance;
  const hasBalanceBreakdown = dailyRemaining != null || permanentBalance != null;
  const quotaDateLabel = formatQuotaDate(summary?.dailyQuotaDate);

  const loadHistory = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setLoadError("");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/points/me/history?limit=8`);
      setHistory(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.warn("[UserPoints] 积分消耗历史加载失败", error);
      setLoadError("暂时无法加载消耗记录");
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    loadHistory();
  }, [loadHistory]);

  const handleBlur = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setInstantPopover(false);
      setOpen(false);
    }
  }, []);

  return (
    <div
      className={`shrink-0 ${className}`}
    >
      <div
        className="relative h-full"
        onMouseEnter={() => {
          setInstantPopover(false);
          handleOpen();
        }}
        onMouseLeave={() => {
          setInstantPopover(false);
          setOpen(false);
        }}
        onFocus={() => {
          setInstantPopover(true);
          handleOpen();
        }}
        onBlur={handleBlur}
      >
        <div
          tabIndex={0}
          role="button"
          aria-label={`总可用积分 ${formatPoints(totalBalance)}，悬停查看余额明细和近期消耗`}
          aria-expanded={open}
          className="inline-flex h-full min-h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-bold outline-none transition-colors"
          style={{
            borderColor: `${accentColor}45`,
            color: "var(--color-text-primary)",
            backgroundColor: `${accentColor}0f`,
          }}
        >
          <Coins size={14} style={{ color: accentColor }} />
          <span>{formatPoints(totalBalance)}</span>
          {showLabel && (
            <span className="hidden 2xl:inline" style={{ color: "var(--color-text-muted)" }}>
              积分
            </span>
          )}
        </div>

        {popoverVisibility.mounted && (
          <div
            className="absolute right-0 top-full z-[80] w-[340px] max-w-[calc(100vw-24px)] pt-2"
          >
            <div
              className="ui-hover-popover overflow-hidden rounded-xl border shadow-2xl"
              data-state={popoverVisibility.exiting ? "closed" : "open"}
              data-instant={instantPopover}
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border-primary)",
              }}
            >
              <div
                className="flex items-center justify-between border-b px-3.5 py-3"
                style={{ borderColor: "var(--color-border-primary)" }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-lg"
                    style={{ color: accentColor, backgroundColor: `${accentColor}16` }}
                  >
                    <ReceiptText size={16} />
                  </span>
                  <div>
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>
                      积分余额
                    </div>
                    <div className="mt-0.5 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                      总可用 {formatPoints(totalBalance)}
                    </div>
                  </div>
                </div>
              </div>

              {hasBalanceBreakdown && (
                <div
                  className="grid grid-cols-2 gap-2 border-b p-2.5"
                  style={{ borderColor: "var(--color-border-primary)" }}
                >
                  <div className="rounded-lg px-2.5 py-2" style={{ backgroundColor: `${accentColor}10` }}>
                    <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>今日剩余</div>
                    <div className="mt-1 text-sm font-bold font-mono-num" style={{ color: accentColor }}>
                      {formatPoints(dailyRemaining)}
                    </div>
                    <div className="mt-1 text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                      {dailyQuota != null
                        ? `每日重置为 ${formatPoints(dailyQuota)}${quotaDateLabel ? ` · ${quotaDateLabel}` : ""}`
                        : "每日免费额度，次日重置"}
                    </div>
                  </div>
                  <div className="rounded-lg px-2.5 py-2" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>永久积分</div>
                    <div className="mt-1 text-sm font-bold font-mono-num" style={{ color: "var(--color-text-primary)" }}>
                      {formatPoints(permanentBalance)}
                    </div>
                    <div className="mt-1 text-[9px]" style={{ color: "var(--color-text-muted)" }}>长期保留，不随每日额度重置</div>
                  </div>
                </div>
              )}

              <div className="max-h-[360px] overflow-y-auto p-2 custom-scrollbar">
                <div className="flex items-center justify-between px-2.5 pb-1.5 pt-0.5">
                  <span className="text-[10px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>近期积分消耗</span>
                  <span className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>最近 8 条</span>
                </div>
                {loading && history.length === 0 && (
                  <div
                    className="flex h-24 items-center justify-center gap-2 text-xs"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    <LoaderCircle size={15} className="animate-spin" />
                    正在加载
                  </div>
                )}

                {!loading && loadError && (
                  <div
                    className="flex h-24 items-center justify-center text-xs"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    {loadError}
                  </div>
                )}

                {!loading && !loadError && history.length === 0 && (
                  <div
                    className="flex h-24 items-center justify-center text-xs"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    暂无积分消耗记录
                  </div>
                )}

                {history.map((item) => {
                  const dailyUsed = Math.abs(Math.min(0, Number(item.dailyDelta || 0)));
                  const permanentUsed = Math.abs(Math.min(0, Number(item.permanentDelta || 0)));
                  return (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 rounded-lg px-2.5 py-2.5 transition-colors hover:bg-[var(--color-bg-tertiary)]"
                    >
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="truncate text-xs font-semibold"
                          style={{ color: "var(--color-text-primary)" }}
                          title={resolveSourceLabel(item)}
                        >
                          {resolveSourceLabel(item)}
                        </span>
                        {item.episodeId != null && (
                          <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[9px]"
                            style={{
                              color: "var(--color-text-muted)",
                              backgroundColor: "var(--color-bg-tertiary)",
                            }}
                          >
                            本集
                          </span>
                        )}
                      </div>
                      <div
                        className="mt-1 truncate text-[10px]"
                        style={{ color: "var(--color-text-muted)" }}
                        title={getPublicVideoLabel(item.model, item.note || "")}
                      >
                        {getPublicVideoLabel(item.model, item.note || "生成任务")}
                      </div>
                      <div className="mt-1 text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                        {formatTime(item.createdAt)}
                      </div>
                      {(dailyUsed > 0 || permanentUsed > 0) && (
                        <div className="mt-1 text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                          {dailyUsed > 0 ? `今日额度 -${formatPoints(dailyUsed)}` : ""}
                          {dailyUsed > 0 && permanentUsed > 0 ? " · " : ""}
                          {permanentUsed > 0 ? `永久积分 -${formatPoints(permanentUsed)}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-bold" style={{ color: "#ef6a6a" }}>
                        -{formatPoints(Math.abs(Number(item.delta || 0)))}
                      </div>
                      <div className="mt-1 text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                        余 {formatPoints(item.balanceAfter)}
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPointBalance;
